import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  flushImageUploadOutbox,
  setImageUploadOutboxSuccessBridge,
  setImageUploadOutboxFailureBridge,
} from "@/lib/image-upload-outbox";
import {
  flushCrmSyncOutbox,
  setCrmSyncOutboxSuccessBridge,
  setCrmSyncOutboxFailureBridge,
} from "@/lib/crm-sync-outbox";
import { getOfflineSyncRequireReview } from "@/lib/offline-sync-settings";
import { runFolderPhotoUploadFollowup } from "@/lib/folder-photo-upload-followup";
import { queryKeys } from "@/lib/query-keys";
import { notify } from "@/lib/notify";

function triggerFlush(): void {
  void flushImageUploadOutbox();
  if (!getOfflineSyncRequireReview()) {
    void flushCrmSyncOutbox();
  }
}

/**
 * Registers outbox flush on online + visibility for both image and CRM sync outboxes,
 * and React Query follow-up on successful replay.
 * Must run under QueryClientProvider.
 */
export function useImageUploadOutboxSync(): void {
  const qc = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    setImageUploadOutboxSuccessBridge((detail) => {
      const { context, response, files } = detail;
      // gallery / deal replays don't know the folderId up-front; the server returns it.
      const responseFolderId = (response as { folderId?: string } | null)?.folderId;
      const followupFolderId = context.folderId || responseFolderId || "";
      void runFolderPhotoUploadFollowup(qc, followupFolderId, response, files).then(() => {
        if (followupFolderId) {
          void qc.invalidateQueries({ queryKey: ["image-folder-photos", followupFolderId] });
        }
        if (context.dealId) {
          void qc.invalidateQueries({ queryKey: ["image-folders", { dealId: context.dealId }] });
          void qc.invalidateQueries({ queryKey: ["image-folders-photos", context.dealId] });
        }
      });
    });

    setCrmSyncOutboxSuccessBridge((detail) => {
      const { op } = detail;
      if (op.type === "note") {
        void qc.invalidateQueries({ queryKey: ["deal-notes", op.dealId] });
        void qc.invalidateQueries({ queryKey: ["deal", op.dealId] });
      } else if (op.type === "deal_patch") {
        void qc.invalidateQueries({ queryKey: ["deal", op.dealId] });
      } else if (op.type === "deal_stage") {
        void qc.invalidateQueries({ queryKey: ["deal", op.dealId] });
        void qc.invalidateQueries({ queryKey: ["crm-board"] });
      }
    });

    // Non-retryable CRM op was dropped (e.g. server rejected a stage move): tell the user
    // so they can redo it manually, and refresh the board/deal to the real state.
    setCrmSyncOutboxFailureBridge((detail) => {
      const { op, errorCode, message } = detail;
      void qc.invalidateQueries({ queryKey: ["deal", op.dealId] });
      void qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
      const action =
        op.type === "deal_stage"
          ? t("uploadOutbox.syncRejectedStage", { defaultValue: "Fázisváltás" })
          : op.type === "note"
            ? t("uploadOutbox.syncRejectedNote", { defaultValue: "Jegyzet" })
            : t("uploadOutbox.syncRejectedPatch", { defaultValue: "Módosítás" });
      const codeHint = errorCode ? ` (${errorCode})` : "";
      const msgHint = message ? ` — ${message}` : "";
      notify.error(
        t("uploadOutbox.syncRejectedTitle", { defaultValue: "Szinkronizálás elutasítva" }),
        `${action}: ${t("uploadOutbox.syncRejectedDescShort", {
          defaultValue: "a szerver elutasította, ellenőrizd kézzel.",
        })}${codeHint}${msgHint}`,
      );
    });

    // Non-retryable image upload was dropped (e.g. file too large / unsupported type).
    setImageUploadOutboxFailureBridge((detail) => {
      const name = detail.fileNames[0] ?? "";
      const codeHint = detail.errorCode ? ` (${detail.errorCode})` : "";
      notify.error(
        t("uploadOutbox.uploadRejectedTitle", { defaultValue: "Kép feltöltés elutasítva" }),
        name
          ? `${name}: ${t("uploadOutbox.uploadRejectedDescShort", {
              defaultValue: "a szerver elutasította.",
            })}${codeHint}`
          : `${t("uploadOutbox.uploadRejectedDescShort", {
              defaultValue: "A szerver elutasította.",
            })}${codeHint}`,
      );
    });

    const onOnline = () => triggerFlush();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        triggerFlush();
      }
    };
    // Best-effort flush when the app is being backgrounded/closed (mobile PWA): the wizard
    // may queue photos and the user immediately leaves before the debounce flush ran.
    const onPageHide = () => {
      void flushImageUploadOutbox();
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    // Periodic safety net while items may still be pending (no Background Sync on iOS).
    const periodic = window.setInterval(() => {
      if (typeof navigator === "undefined" || navigator.onLine) triggerFlush();
    }, 30_000);
    triggerFlush();

    return () => {
      setImageUploadOutboxSuccessBridge(null);
      setImageUploadOutboxFailureBridge(null);
      setCrmSyncOutboxSuccessBridge(null);
      setCrmSyncOutboxFailureBridge(null);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.clearInterval(periodic);
    };
  }, [qc, t]);
}
